import React, { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { cn } from "../lib/utils";
import { handwritten } from "./editorial";
import { TextReveal } from "./TextReveal";
import {
  FeedVisual,
  MapVisual,
  CreateVisual,
  CommunitiesVisual,
  YouVisual,
} from "./AppMocks";

const Features: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const scaleY = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  return (
    <section
      ref={containerRef}
      className="py-24 md:py-40 px-6 md:px-12 max-w-[1400px] mx-auto bg-paper relative"
    >
      {/* Section Header */}
      <div className="mb-32 max-w-3xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-center gap-4 mb-6"
        >
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-salmon whitespace-nowrap">
            04: How the loop works
          </span>
          <span className="flex-1 h-px bg-ink/15" />
        </motion.div>
        <p
          className="text-2xl md:text-3xl text-salmon -rotate-1 mb-2"
          style={handwritten}
        >
          five tabs, one feeling
        </p>
        <h2 className="text-5xl md:text-7xl font-serif tracking-tight mb-6 leading-[1]">
          <TextReveal>I have</TextReveal>
          <TextReveal delay={0.1}>
            <span className="italic text-salmon">somewhere to be.</span>
          </TextReveal>
        </h2>
        <p className="text-xl md:text-2xl text-gray-700 max-w-2xl">
          The app is five tabs. Each one removes one of the reasons you
          haven&rsquo;t left the house this week.
        </p>
      </div>

      {/* Vertical Timeline Line */}
      <div className="absolute left-[50%] top-[560px] bottom-24 w-[2px] bg-ink/10 hidden md:block -translate-x-1/2 z-0">
        <motion.div
          style={{ scaleY, transformOrigin: "top" }}
          className="w-full h-full bg-salmon"
        />
      </div>

      <div className="flex flex-col gap-32 md:gap-48 relative z-10">
        <TimelineItem
          step="01"
          title="A feed of plans, not posts"
          subtitle={
            <>
              <strong className="text-ink">The Home tab.</strong>
              <br />
              <br />
              Loops from communities you&rsquo;ve joined and people you&rsquo;ve
              looped with. Tap a card. Loop in. That&rsquo;s the whole app.
            </>
          }
          align="left"
        >
          <FeedVisual />
        </TimelineItem>

        <TimelineItem
          step="02"
          title="A map of right-now"
          subtitle={
            <>
              <strong className="text-ink">The Map tab.</strong>
              <br />
              <br />
              See what&rsquo;s happening within a 10-minute walk. The closest
              one wins, every time. Your home and work are hidden by a privacy
              zone.
            </>
          }
          align="right"
        >
          <MapVisual />
        </TimelineItem>

        <TimelineItem
          step="03"
          title="Post one in seconds"
          subtitle={
            <>
              <strong className="text-ink">The Create tab.</strong>
              <br />
              <br />
              Going for a coffee? Picnic in the park? Tap the centre button,
              drop a photo, set the spot, hit go. Three taps, no friction.
            </>
          }
          align="left"
        >
          <CreateVisual />
        </TimelineItem>

        <TimelineItem
          step="04"
          title="Communities that actually meet"
          subtitle={
            <>
              <strong className="text-ink">The Communities tab.</strong>
              <br />
              <br />
              Run clubs, book clubs, supper crews. Real, recurring loops, not a
              dormant group chat with 300 muted members.
            </>
          }
          align="right"
        >
          <CommunitiesVisual />
        </TimelineItem>

        <TimelineItem
          step="05"
          title="People you've looped with"
          subtitle={
            <>
              <strong className="text-ink">The You tab.</strong>
              <br />
              <br />
              Your profile remembers who you&rsquo;ve shown up with. Build a
              graph of people who actually share your time, not just your
              contacts.
            </>
          }
          align="left"
        >
          <YouVisual />
        </TimelineItem>
      </div>
    </section>
  );
};

interface TimelineItemProps {
  step: string;
  title: string;
  subtitle: React.ReactNode;
  align: "left" | "right";
  children: React.ReactNode;
}

const TimelineItem: React.FC<TimelineItemProps> = ({
  step,
  title,
  subtitle,
  align,
  children,
}) => {
  const isLeft = align === "left";

  return (
    <div
      className={cn(
        "flex flex-col md:flex-row items-center gap-12 md:gap-24",
        !isLeft && "md:flex-row-reverse",
      )}
    >
      {/* Text Side */}
      <div className="flex-1 text-center md:text-left">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-20%" }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-6xl font-serif text-salmon/20 font-bold block mb-4">
            {step}
          </span>
          <h3 className="text-4xl md:text-5xl font-medium tracking-tight mb-6">
            {title}
          </h3>
          <div className="text-lg text-gray-500 leading-relaxed font-light">
            {subtitle}
          </div>
        </motion.div>
      </div>

      {/* Visual Side */}
      <div className="flex-1 w-full">
        <div
          className="relative md:aspect-[4/3] rounded-3xl border-2 border-ink/10 overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-500 group min-h-[440px] sm:min-h-[520px] md:min-h-0"
          style={{ backgroundColor: "var(--color-paper-tan)" }}
        >
          <div className="md:absolute md:inset-0 flex items-center justify-center min-h-[440px] sm:min-h-[520px] md:min-h-0 p-8">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;
